import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { zipObject } from 'lodash-es';
import { ConfigProvider, message } from '@fuxi/eevee-ui';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAntLocale } from '@fuxi/eevee-hooks';
import {
  getCustomUserInfoUrl,
  getHmacHeaders,
  LOGIN_BASE_URL,
  LoginInfoContextProvider,
  SubPackageSlugEnum,
  USER_ACCOUNT_URL,
} from '@yaotai/common-utils';
import zhAntd from 'antd/es/locale/zh_CN';
import enAntd from 'antd/es/locale/en_US';
import 'dayjs/esm/locale/zh-cn';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import dayjsRelativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';

import { createService } from 'common-package/src/services';
import { YaotaiGlobalContext } from '@commonPackage/contexts';
import * as Sentry from '@sentry/react';
import { ANTD_THEME, URS_SCRIPT_URL } from '@commonPackage/constants';
import { useScriptInjectRetry } from '@commonPackage/hooks/useScriptInjectRetry';
import {
  getCenterCurrentRedirectLoginUrl,
  getTrackEnv,
  getYaotaiRbacCookieDomain,
  initSentry,
} from '@commonPackage/utils';
import { initETracker, Platform, setTrackerConfig, TrackerPidEnum } from '@yaotai/yaotai-tracker';
import { GateWayInstance, RbacGateWayInstance, LoginGateWayInstance } from '@/service/axiosInstances';
import ROUTE_CONFIG from '@/router/config';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(dayjsRelativeTime);
dayjs.extend(advancedFormat);
dayjs.extend(duration);

initSentry({
  dsn: 'https://e8eb9c1b49de45a8b35f50f533cef5a9@sentry.fuxi.netease.com/20',
  env: getTrackEnv(import.meta.env.VITE_APP_MODE ?? ''),
});

initETracker({
  platform: Platform.PC,
  pid: TrackerPidEnum.YaotaiPortal,
  requiredFields: ['pid'], // 当年设置pid后才会开始发送
  env: getTrackEnv(import.meta.env.VITE_APP_MODE ?? ''),
});

const queryClient = new QueryClient();

createService({
  GateWayInstance,
  RbacGateWayInstance,
  LoginGateWayInstance,
});

message.config({
  top: 66,
});

const router = createBrowserRouter(ROUTE_CONFIG, {
  basename: `/${SubPackageSlugEnum.Center}/`,
});
const App = () => {
  const { language, langList } = useAntLocale();
  const langAntList = [zhAntd, enAntd];
  const antLocaleConfigMap = zipObject(langList, langAntList);

  useScriptInjectRetry({
    scriptUrl: URS_SCRIPT_URL,
    successJudge: () => !!window.URS,
  });

  return (
    <DndProvider backend={HTML5Backend}>
      <ConfigProvider theme={ANTD_THEME} locale={antLocaleConfigMap[language] || zhAntd}>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <LoginInfoContextProvider
            isUrs4login={false}
            cookieDomain={getYaotaiRbacCookieDomain()}
            afterGettingHook={(data: any) => {
              Sentry.setUser(data);
              setTrackerConfig({
                uid: data?.uid,
                username: data?.name,
              });
            }}
            autoNoAuthRedirect={false}
            beforeGettingHookErrorCb={() => {
              window.location.href = getCenterCurrentRedirectLoginUrl();
            }}
            customUserInfoUrl={getCustomUserInfoUrl({ gateWayUrl: import.meta.env.VITE_GATEWAY_BASE_URL })}
            customNoAuthRedirectUrl={getCenterCurrentRedirectLoginUrl()}
            getCustomUserInfoHeaders={() =>
              getHmacHeaders({
                url: USER_ACCOUNT_URL,
                baseURL: `${
                  import.meta.env.VITE_GATEWAY_BASE_URL ? import.meta.env.VITE_GATEWAY_BASE_URL : ''
                }${LOGIN_BASE_URL}`,
                secret: import.meta.env.VITE_YEYING_SECRET_KEY!,
                username: import.meta.env.VITE_YEYING_SECRET_USERNAME!,
                apiKey: import.meta.env.VITE_YEYING_API_KEY!,
              })
            }>
            <YaotaiGlobalContext.Provider
              initialState={{
                isMainVersion: import.meta.env.VITE_MAIN === 'true',
                monorepoType: 'center',
                mediaType: 'pc',
                spaceAuthOptions: {
                  isDingzhiWithSpace: import.meta.env.VITE_NEED_CORPORATOR === 'true',
                },
              }}>
              <RouterProvider router={router} />
            </YaotaiGlobalContext.Provider>
          </LoginInfoContextProvider>
        </QueryClientProvider>
      </ConfigProvider>
    </DndProvider>
  );
};

export default App;
